<template>
  <section id="feature-selection" class="page-section selection-section">
    <div class="section-header" id="feature-selection-header">
      <h3>
        Let's get started! Choose where and how you want to engage your audience
      </h3>
    </div>

    <!-- Boundary Selection (Geo / Instant) -->
    <div class="boundary-picker">
      <FeatureCard
        v-for="option in boundaryItems"
        :key="option.id"
        :icon="option.icon"
        :label="option.label"
        :description="option.description"
        :color="option.color"
        :image="option.image"
        :is-active="selectedBoundaryId === option.id"
        :isSelected="selectedBoundaryId === option.id"
        :isDisabled="isDisabled"
        @select="onBoundarySelect(option.id)"
      />
    </div>

    <!-- Experience Selection (AR / Map / Quiz / Survey) -->
    <div class="experience-picker">
      <h4 class="experience-heading">What type of experience?</h4>
      <div class="experience-grid">
        <ServiceCard
          v-for="option in experiences"
          :key="option.id"
          :icon="option.icon"
          :label="option.label"
          :description="option.description"
          :color="option.color"
          :isSelected="selectedExperienceId === option.id"
          :isDisabled="isDisabled"
          @select="onExperienceSelect(option.id)"
        />
      </div>
    </div>

  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import FeatureCard from '@/components/campaign-selector/FeatureCard.vue'
import ServiceCard from '@/components/campaign-selector/ServiceCard.vue'
import type { SelectionOption, ExperienceOption, CampaignExperience } from '@/types/campaign.types'

const props = defineProps<{
  features: SelectionOption[]
  selectedFeatureId?: string | null
  experiences?: ExperienceOption[]
  selectedExperienceId?: CampaignExperience | null
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'select', id: string): void
  (e: 'select-experience', experienceId: CampaignExperience): void
}>()

const isDisabled = computed(() => props.disabled ?? false)

const selectedBoundaryId = computed(() => props.selectedFeatureId ?? null)

const boundaryItems = computed(() =>
  props.features.map((f) => ({
    id: f.id,
    label: f.label,
    description: f.description,
    icon: f.icon,
    color: f.color,
    image: (f as any).image ?? '',
    isDisabled: isDisabled.value
  }))
)

const onBoundarySelect = (id: string) => {
  if (!isDisabled.value) {
    emit('select', id)
  }
}

const onExperienceSelect = (experienceId: CampaignExperience) => {
  if (!isDisabled.value) {
    emit('select-experience', experienceId)
  }
}
</script>

<style scoped>
#feature-selection {
  justify-content: center;
  align-items: center;
  padding-top: 0;
  padding-bottom: calc(var(--footer-clearance) + 3dvh);
  display: flex;
  flex-direction: column;
  gap: clamp(0.75rem, 2dvh, 1.5rem);
  max-height: var(--section-available-height);
  overflow: visible;
}

#feature-selection-header {
  margin: 0;
  width: 100%;
  max-width: min(100%, 46rem);
  text-align: center;
  margin-inline: auto;
  flex-shrink: 1;
  position: relative;
  z-index: 2;
}

#feature-selection-header h3 {
  font-size: clamp(1.05rem, 0.8rem + 1.1vw, 1.75rem);
  line-height: 1.16;
  color: var(--color-heading);
  font-weight: 800;
  margin-bottom: 0;
}

/* ── Boundary picker: side-by-side cards ── */
.boundary-picker {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: clamp(0.5rem, 1.5vw, 1rem);
  width: 100%;
  max-width: min(100%, 30rem);
  margin-inline: auto;
  flex: 0 0 auto;
}

/* ── Experience picker: appears below boundary ── */
.experience-picker {
  width: 100%;
  max-width: min(100%, 36rem);
  margin-inline: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.4rem, 1dvh, 0.75rem);
  flex: 0 1 auto;
  min-height: 0;
}

.experience-heading {
  font-size: clamp(0.9rem, 0.78rem + 0.65vw, 1.25rem);
  line-height: 1.2;
  color: var(--color-heading);
  font-weight: 700;
  margin: 0;
  text-align: center;
  flex-shrink: 0;
}

.experience-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(0.5rem, 1.5vw, 1rem);
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
}

.experience-grid :deep(.service-card) {
  min-height: 0;
  height: 100%;
}

/* ── Reveal transition ── */
.experience-reveal-enter-active {
  transition: opacity 300ms ease, transform 300ms ease;
}

.experience-reveal-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}

.experience-reveal-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.experience-reveal-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ── Mobile adjustments ── */
@media (max-width: 768px) {
  #feature-selection-header {
    width: 100% !important;
  }

  #feature-selection-header h3 {
    font-size: clamp(1rem, 0.9rem + 0.8vw, 1.3rem);
    line-height: 1.18;
  }

  .boundary-picker {
    grid-auto-rows: 1fr;
    align-items: stretch;
  }

  .experience-grid {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }

  .experience-grid :deep(.service-card) {
    min-height: 2.75rem;
  }
}
</style>
